import {useCallback, useEffect} from 'react';
import {EmailSubscriptionsServiceClientImpl} from '../../../grpc_client/grips/protos/email_subscriptions/v1/email_subscriptions_service';
import {
  useGripsClientContext,
  useGrpcClientChannel,
} from '../../../ui/sharedContexts/GrpcClientContext';
import {useUserSessionContext} from '../../../ui/sharedContexts/UserSessionContext';
import {useDevice} from '../../../utils/hooks/useDevice';
import {useMemoizedFunction} from '../../../utils/hooks/useMemoizedFunction';
import {Log} from '../../../utils/logger/log';
import {useDropsSubscriptionsContext} from '../context/SubscriptionsProvider';
import {filterOnlySubscribedDropTypes} from '../utils/helpers';

export const useAreDropsSubscriptionsForUser = (
  shouldCheckOnDemand?: boolean,
) => {
  const channel = useGrpcClientChannel();
  const {childShopId} = useGripsClientContext();
  const {device} = useDevice();
  const {ayUser, setSubscriptions} = useDropsSubscriptionsContext();
  const {email} = useUserSessionContext();
  const setSubscriptionsMemoized = useMemoizedFunction(setSubscriptions);

  const getEmailsForStatusCheck = useCallback(() => {
    if (email) {
      return [email].concat(
        Object.entries(ayUser?.customEmails || {})
          .filter(customEmailEntry => customEmailEntry[1].includes('all'))
          .map(entry => entry[0]),
      );
    }

    return [];
  }, [email, ayUser?.customEmails]);

  useEffect(() => {
    const emails = getEmailsForStatusCheck();

    if (!email || emails.length === 0 || shouldCheckOnDemand === false) {
      return undefined;
    }

    const promises = [];

    const client = new EmailSubscriptionsServiceClientImpl(channel);

    for (const customEmail of emails) {
      promises.push(
        client.getEmailSubscriptions({
          childShopId,
          device,
          email: customEmail,
        }),
      );
    }

    Promise.all(promises)
      .then(datas => {
        datas.forEach(data => {
          if (
            !data.subscriptions?.isEmailConfirmationPending &&
            data.subscriptions?.subscriptions?.length &&
            data.subscriptions.subscriptions.length > 0
          ) {
            const subscribedTypes = filterOnlySubscribedDropTypes(
              data.subscriptions?.subscriptions,
            );
            setSubscriptionsMemoized.current({
              ayUser: {
                allDrops: {
                  isSubscribed: true,
                  subscribedTypes,
                },
                customEmails: {...ayUser?.customEmails},
                individualDrops: {...ayUser?.individualDrops},
                email,
              },
            });
          }
        });
      })
      .catch(_error => {
        Log.error('Error in get email subscription service', {
          error: new Error(_error),
          message: _error.message,
          statusCode: _error.code,
        });
      });
  }, [
    channel,
    childShopId,
    device,
    email,
    shouldCheckOnDemand,
    setSubscriptionsMemoized,
  ]);
};
